import FingerprintJS from "@fingerprintjs/fingerprintjs";

import { LogSentryMessage } from "./sentryLogger";
import { uuidv4 } from "./uuid";

const FINGERPRINT_TIMEOUT_MS = 1000;
const FALLBACK_STORAGE_KEY = "browser-fp-fallback-id";

let fingerprintId: string | null = null;
let fingerprintPromise: Promise<string> | null = null;

function readFallback(): string | null {
  try {
    return window.sessionStorage.getItem(FALLBACK_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeFallback(id: string) {
  try {
    window.sessionStorage.setItem(FALLBACK_STORAGE_KEY, id);
  } catch {
    // sessionStorage unavailable
  }
}

function getOrCreateFallback(): string {
  const stored = readFallback();
  if (stored) return stored;

  const id = uuidv4();
  writeFallback(id);
  return id;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Fingerprint timeout")), ms);
    promise.then(resolve, reject).finally(() => clearTimeout(timer));
  });
}

async function resolveFingerprint(): Promise<string> {
  const agent = await FingerprintJS.load();
  const result = await agent.get();
  return result.visitorId;
}

export function getFingerprintAsync(): Promise<string> {
  if (fingerprintId) {
    return Promise.resolve(fingerprintId);
  }

  if (!fingerprintPromise) {
    fingerprintPromise = withTimeout(resolveFingerprint(), FINGERPRINT_TIMEOUT_MS)
      .then((visitorId) => {
        fingerprintId = visitorId;
        return visitorId;
      })
      .catch((error) => {
        fingerprintPromise = null;
        const reason = error instanceof Error ? error.message : String(error);
        LogSentryMessage({ message: `Fingerprint loading failed: ${reason}`, level: "warning" });
        const fallback = getOrCreateFallback();
        fingerprintId = fallback;
        return fallback;
      });
  }

  return fingerprintPromise;
}

export function getFingerprint(): string | null {
  return fingerprintId;
}
